import { writable, get, type Writable } from 'svelte/store';

export type Store<T> = {
  get: (key: keyof T) => T[keyof T] | undefined;
  set: (value: Partial<T>) => void;
  clear: () => void;
  all: () => T | Partial<T>;
  push: (patchData: Partial<T>) => void;
  remove: (key: keyof T) => void;
};

// Define the createStore function
export const createStore = <T>(initialValue: Partial<T>): Writable<Partial<T>> & Store<T> => {
  const store = writable<Partial<T>>(initialValue);

  return {
    ...store,

    get: (key: keyof T) => {
      const items = get(store);
      if (items && key in items) {
        const value = items[key];
        return value !== undefined ? value : undefined;
      }
      return undefined;
    },
    set: (value: Partial<T>) => store.set(value),
    clear: () => store.set({} as T), // Set to an empty object instead of null
    all: () => get(store),
    push: (patchData: Partial<T>) => {
      store.update((currentState) => {
        // Handle nested objects correctly
        const mergeDeep = (target: any, source: any) => {
          for (const key of Object.keys(source)) {
            if (source[key] instanceof Object && key in target) {
              Object.assign(source[key], mergeDeep(target[key], source[key]));
            }
          }
          // Join `target` and `source`
          Object.assign(target || {}, source);
          return target;
        };
        return mergeDeep({ ...currentState }, patchData);
      });
    },
    remove: (key: keyof T) => {
      store.update((currentState: any) => {
        if (!(key in currentState)) return currentState;
        const { [key]: _, ...rest } = currentState;
        return rest;
      });
    }
  };
};
